import React, { useState, useCallback, useEffect, useRef, memo, useMemo } from "react";

import { format, addDays, startOfMonth, isSameDay, addMinutes } from "date-fns";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { AnimatePresence, motion } from "framer-motion";

import { ChevronDown, Settings, SquarePen, Check } from "lucide-react";
import { useDrag, usePlanner } from "../contexts/PlannerContext";
import { useCourses } from "@/lib/courses";
import { DueItemContent } from "./DueView";
import { CalendarColumn, CalendarColumnHeader, CalendarRowLines, CalendarTimeIndicators, NonCalendarDragPreview } from "./Calendar";
import { AutoPlanSettingsDialog } from "./Settings";
import { MiniMonthPicker, MINI_MONTH_WIDTH } from "./MiniMonthPicker";
//import { AssignmentPlanned, defaultTaskValues } from "@/lib/customassignments";
import { AssignmentPlanned, defaultTaskValues } from "@/lib/types";
import { useCalendarEvents } from "../../../../hooks/useCalendarEvents.ts";
//import { GoogleCalendarSyncDialog } from "./GoogleCalendarSyncDialog";
//import { useGoogleCalendarStorage } from "../../../../hooks/useGoogleCalendarStorage.ts";
import { UNRESTRICTED_LEARNPLUS_TRIAL } from "@/lib/restrictions";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { isTakeHomeAssignment } from "../utils/planningType";
import { DoneSvg, IncompleteSvg, ProgressSvg } from "@/lib/svg";

/*
function autoScheduleTasks(unplanned: any[], items: any[], settings: any = {}) {
    const { offset = 1, timing = "morning" } = settings;
    const timingPreferences: { [key: string]: number } = {
        "morning": 9,    // 9 AM
        "midday": 12,   // 12 PM
        "evening": 18,   // 6 PM  
        "night": 22,     // 10 PM
        "all-day": 9     // Default to 9 AM
    };

    const output = [...items];

    const taskAllocationPerDay: any = {};
    const today = new Date();
    unplanned.forEach((task) => {
        const taskDate = new Date(task.time);
        const diff = Math.max(0, differenceInDays(taskDate, today) - offset);

        taskAllocationPerDay[diff] = {
            "total": (taskAllocationPerDay[diff]?.["total"] || 0) + 1,
            "duration": [...(taskAllocationPerDay[diff]?.["duration"] || []), task.duration]
        }
    });

    let startIndex = 0;
    Object.keys(taskAllocationPerDay).forEach((elaspedDays) => {

        const recommendedTime = timingPreferences[timing];
        const currentTime = new Date(today.getFullYear(), today.getMonth(), today.getDate() + parseInt(elaspedDays), recommendedTime, 0);
        for (let i = 0; i < taskAllocationPerDay[elaspedDays]["total"]; i++) {
            const index = startIndex + i;
            const task = unplanned[index];
            console.log("task", task.title);
            //const time = findClosestAvailableTime(task.id, currentTime);
            const time = currentTime;
            if (!time) {
                console.error("no time could be scheduled for task", task.title);
                return;
            }
            output[index] = {
                ...task,
                plannedAt: new Date(time).toISOString(),
            }
            currentTime.setMinutes(currentTime.getMinutes() + task.duration + 15);
        }
        startIndex += taskAllocationPerDay[elaspedDays]["total"];
    });

    return output;
}
    */

const AutoScheduleButton = ({ tasks }: { tasks: any[] }) => {
    const { autoScheduleTasks, undoAutoPlannedAssignments } = usePlanner();
    const { restrictedActionCheck, openRestrictedDialog, withEventCooldown, trackEvent, checkGate } = useCommunication();

    const [autoPlanned, setAutoPlanned] = useState<Record<string, string> | null>(null);
    const [isWorking, setIsWorking] = useState(false);
    const undoTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (undoTimeoutRef.current) {
                window.clearTimeout(undoTimeoutRef.current);
                undoTimeoutRef.current = null;
            }
        };
    }, []);

    const clearUndo = useCallback(() => {
        if (undoTimeoutRef.current) {
            window.clearTimeout(undoTimeoutRef.current);
            undoTimeoutRef.current = null;
        }
        setAutoPlanned(null);
    }, []);

    const handleAutoSchedule = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isWorking) return;

        const { restricted } = await checkGate("assignments_auto_schedule");
        if (restricted) return;

        // Show Undo immediately (even while scheduling) to reduce perceived delay.
        // The actual undo payload gets filled in once scheduling completes.
        setAutoPlanned({});
        if (undoTimeoutRef.current) window.clearTimeout(undoTimeoutRef.current);
        undoTimeoutRef.current = window.setTimeout(() => {
            setAutoPlanned(null);
            undoTimeoutRef.current = null;
        }, 5000);

        setIsWorking(true);
        try {
            const unplannedVisibleTasks = tasks.filter((item: any) => item?.current?.plannedAt === null);
            const planned = await autoScheduleTasks(unplannedVisibleTasks);

            const plannedIds = Object.keys(planned || {});
            if (plannedIds.length > 0) {
                // Only set the undo payload if the 5s window hasn't expired yet.
                if (undoTimeoutRef.current) {
                    setAutoPlanned(planned);
                }
            } else {
                clearUndo();
            }
        } finally {
            setIsWorking(false);
        }

        withEventCooldown("assignments_auto_plan_clicked", async () => {
            await trackEvent("assignments_auto_plan_clicked");
        }, 30);
    };

    const handleUndo = async () => {
        if (!autoPlanned || isWorking) return;
        setIsWorking(true);
        try {
            await undoAutoPlannedAssignments(autoPlanned);
        } finally {
            setIsWorking(false);
            clearUndo();
        }
    };

    return (
        <div className="flex items-center gap-[10px] w-full">
            <button
                onClick={autoPlanned ? handleUndo : handleAutoSchedule}
                disabled={isWorking}
                className="text-sm cursor-pointer py-[10px] bg-theme-1 rounded-full border border-theme flex items-center justify-center flex-1 hover:bg-theme-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={autoPlanned ? "undo" : "auto"}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.14, ease: "easeInOut" }}
                    >
                        {autoPlanned ? "Undo" : "Auto Plan All"}
                    </motion.span>
                </AnimatePresence>
            </button>
            <AutoPlanSettingsDialog
                trigger={(
                    <button className="cursor-pointer hover:bg-theme-1 transition-colors rounded-full p-[10px] bg-theme-1 border border-theme">
                        <Settings className="h-[16px] w-[16px]" />
                    </button>
                )}
            />
        </div>
    );
};

const UnassignedTask = memo(({ item }: { item: any }) => {

    const { loadSharedComponent, checkGate } = useCommunication();
    const { autoScheduleTasks } = usePlanner();

    const handleAutoScheduleItem = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const response = await checkGate("assignments_auto_schedule");
        if (response.restricted) return;
        autoScheduleTasks([item]);
    }, [item, autoScheduleTasks, checkGate]);

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: "unassigned_task_" + item.current.id,
        data: {
            "type": "unassigned-task",
            "planner": {
                "id": item?.current?.id,
                "title": item?.current?.title,
                "course": item?.current?.course,
                "duration": item?.current?.duration || 60,
                "plannedAt": item?.current?.plannedAt ?? null,
                "status": item?.current?.status ?? "incomplete",
            }
        }
    });

    const assignmentUrl = item?.current?.url || item?.original?.url;

    return (
        <div
            onClick={() => {
                if (assignmentUrl) {
                    window.open(assignmentUrl, "_blank", "noopener,noreferrer");
                } else {
                    loadSharedComponent("AssignmentCreator", { assignment: item });
                }
            }}
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={`p-[1px] pl-0 rounded-xl bg-theme-1 flex flex-col relative overflow-hidden ${isDragging ? 'opacity-50' : ''}`}
        >
            <DueItemContent item={item} onAutoSchedule={handleAutoScheduleItem} onEdit={() => loadSharedComponent("AssignmentCreator", { assignment: item })} />
            <div className={`absolute top-0 right-0 h-full w-full z-0 bg-theme-link opacity-[.22]`} />
        </div>
    )

    /*
    return (
        <Dialog>
            <DialogTrigger asChild>
                <div ref={setNodeRef} {...listeners} {...attributes} className={`p-[2px] rounded-xl bg-theme-1 p-[3px] flex flex-col gap-[5px] ${isDragging ? 'opacity-50' : ''}`}>
                    <DueItemContent item={item} onAutoSchedule={() => { }} />
                </div>
            </DialogTrigger>
            <DialogContent className="bg-none p-0 border-none shadow-none max-w-min">
                <Creator assignment={item} />
            </DialogContent>
        </Dialog>
    )
        */
});

const areSameDay = (date1: Date, date2: Date) => {
    return date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

const formatDurationShort = (minutes: number) => {
    const hour = Math.floor(minutes / 60);
    const min = minutes % 60;
    return `${hour > 0 ? `${hour}h` : ""}${min > 0 ? ` ${min}m` : ""}`;
};

const PlannerColumnHeaders = ({ scrollLeft }: { scrollLeft: number }) => {
    const { startDate, blockWidth, settings } = usePlanner();
    const today = new Date();

    const calendarViewType = settings?.assignments_calendar_view_type || "week";
    const weekDays = settings?.assignments_week_days || 7;
    const effectiveWeekDays = calendarViewType === "5day" ? 5 : weekDays;
    const visibleColumnOffset = Math.floor(Math.abs(scrollLeft) / blockWidth);

    const render = useMemo(() => {
        const ds: any[] = [];

        const days = Array.from({ length: effectiveWeekDays }, (_, i) => {
            const date = addDays(startDate, i + visibleColumnOffset);
            ds.push(date.getDate());
            return <CalendarColumnHeader key={i} date={date} today={areSameDay(date, today)} />;
        });

        return days;
    }, [startDate, today, visibleColumnOffset, effectiveWeekDays]);

    return (
        <div className="flex h-full absolute top-0 z-20 min-w-0 max-w-full" style={{ left: `${visibleColumnOffset * blockWidth}px` }}>
            {render}
        </div>
    );
};

const DueRowContent = ({
    dates,
    takeHomeMap,
    blockWidth,
    offsetLeft = 0,
}: {
    dates: Date[],
    takeHomeMap: Map<string, any[]>,
    blockWidth: number,
    offsetLeft?: number,
}) => {
    const { courseData } = useCourses();
    const { loadSharedComponent } = useCommunication();

    const renderChip = (item: any, keySuffix: string) => {
        const courseColor = courseData?.[item?.current?.course]?.color || "gray";
        const dueTime = item?.current?.due ? format(new Date(item.current.due), "h:mmaaa") : "";
        const isComplete = item?.current?.status === "complete" || item?.current?.status === "graded";

        return (
            <button
                key={(item?.current?.id || item?.current?.title || "due-item") + keySuffix}
                className="flex items-center justify-between gap-[6px] rounded-[8px] px-[8px] py-[3px] border text-theme-0 text-left w-full min-w-0 cursor-pointer hover:brightness-95 transition-all duration-150 text-[12px] leading-[16px] bg-theme-1"
                style={{ borderColor: courseColor }}
                onClick={() => loadSharedComponent("AssignmentCreator", { assignment: item })}
            >
                <span className="h-[10px] w-[10px] rounded-full flex-shrink-0" style={{ background: courseColor }} />
                <span className={`flex-1 min-w-0 font-semibold truncate ${isComplete ? "line-through" : ""}`}>
                    {item?.current?.title || "Due item"}
                </span>
                {dueTime && <span className="text-[11px] text-theme-1 whitespace-nowrap">{dueTime}</span>}
            </button>
        );
    };

    return (
        <div
            className="absolute top-0 bottom-0 bg-theme-0 z-25"
            style={{ left: `${offsetLeft}px`, width: `${dates.length * blockWidth}px` }}
        >
            <div className="flex h-full">
                {dates.map((date) => {
                    const key = date.toISOString();
                    const items = takeHomeMap.get(key) || [];
                    return (
                        <div key={key} className="px-2 py-2 flex flex-col gap-[4px]" style={{ width: `${blockWidth}px` }}>
                            {items.map((item: any, itemIdx: number) => renderChip(item, `${key}-${itemIdx}`))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const HEADER_HEIGHT = 40;
const MIN_DUE_ROW_HEIGHT = 32;
const DUE_CHIP_HEIGHT = 26;
const DUE_CHIP_GAP = 4;
const DUE_ROW_PADDING_Y = 6;
const SIDEBAR_PADDING_RIGHT = 40;
const SIDEBAR_PADDING_X = 16 + SIDEBAR_PADDING_RIGHT;

const useSidebarSizing = () => {
    const [viewportWidth, setViewportWidth] = useState<number>(() => (typeof window !== "undefined" ? window.innerWidth : 1400));

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const sidebarMinWidth = MINI_MONTH_WIDTH + SIDEBAR_PADDING_X;
    const rightPanelWidth = useMemo(() => {
        const minWidth = sidebarMinWidth;
        const maxWidth = Math.max(minWidth, 360);
        const dynamic = viewportWidth * 0.2;
        return Math.max(minWidth, Math.min(dynamic, maxWidth));
    }, [sidebarMinWidth, viewportWidth]);

    return { viewportWidth, rightPanelWidth, sidebarMinWidth };
};

const MonthGridView = () => {
    const { startDate, planned, overrides, dueItems, scrollArea, scrollPos, portal } = usePlanner();
    const { schedule, courseData } = useCourses();

    // Fill missing planned metadata (title/course/status/duration) from the corresponding due item

    const dueItemLookup = useMemo(() => {
        const map = new Map<string, any>();
        dueItems.forEach((item: any) => {
            const id = (item?.current?.id ?? item?.original?.id)?.toString();
            if (id) map.set(id, item);
        });
        return map;
    }, [dueItems]);

    const mergePlannedWithDetails = (key: string, plannedItem: any) => {
        const fallback = dueItemLookup.get(key);
        const source = fallback?.current || fallback?.original || {};
        return {
            id: key,
            ...plannedItem,
            course: plannedItem.course ?? source.course,
            title: plannedItem.title ?? source.title,
            status: plannedItem.status ?? source.status,
            duration: plannedItem.duration ?? source.duration ?? defaultTaskValues.duration,
        };
    };

    const monthStart = useMemo(() => startOfMonth(startDate), [startDate]);
    const calendarStartDate = useMemo(() => {
        const start = new Date(monthStart);
        start.setDate(start.getDate() - start.getDay());
        return start;
    }, [monthStart]);
    const calendarEndDate = useMemo(() => addDays(calendarStartDate, 42), [calendarStartDate]);

    const { events: calendarEvents } = useCalendarEvents(calendarStartDate, calendarEndDate);

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    const areSameDay = (date1: Date, date2: Date) => {
        return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
    };

    const weeks = useMemo(() => {
        const weeksArray = [];
        let currentWeekStart = new Date(calendarStartDate);

        for (let week = 0; week < 6; week++) {
            const days = [];
            for (let day = 0; day < 7; day++) {
                const date = addDays(currentWeekStart, day);
                const isCurrentMonth = date.getMonth() === monthStart.getMonth();
                const isToday = areSameDay(date, todayDate);
                const isSelected = areSameDay(date, startDate);

                const items: AssignmentPlanned[] = Object.keys(planned)
                    .filter((key) => {
                        const item = planned[key];
                        if (!item.plannedAt) return false;
                        const itemDate = new Date(item.plannedAt);
                        return areSameDay(itemDate, date);
                    })
                    .map((key) => ({
                        ...mergePlannedWithDetails(key, planned[key]),
                        ...(overrides[key]?.["title"] ? { title: overrides[key]?.["title"] } : {}),
                        ...(overrides[key]?.["course"] ? { course: overrides[key]?.["course"] } : {}),
                        ...(overrides[key]?.["status"] ? { status: overrides[key]?.["status"] } : {}),
                    }));

                const due = dueItems.filter((item: any) => {
                    const dueDate = item?.current?.due ? new Date(item.current.due) : null;
                    return dueDate && areSameDay(dueDate, date);
                });

                const scheduleItems = (schedule?.[date.getDay()] || []).map((item: any) => {
                    const start = new Date(date);
                    start.setHours(Math.floor(item.start / 60), item.start % 60, 0, 0);
                    const end = new Date(date);
                    end.setHours(Math.floor(item.end / 60), item.end % 60, 0, 0);
                    return { ...item, startDate: start, endDate: end };
                });

                const externalCalendarItems = calendarEvents
                    .filter((event) => areSameDay(event.startDate, date) && !event.isAllDay)
                    .map((event) => ({
                        id: event.id,
                        title: event.title,
                        plannedAt: event.startDate.toISOString(),
                        duration: event.duration,
                        course: event.source === 'google' ? 'google_calendar' : 'microsoft_calendar',
                        isGoogleEvent: event.isGoogleEvent,
                        isMicrosoftEvent: event.isMicrosoftEvent,
                        calendarColor: event.calendarColor,
                        htmlLink: event.htmlLink,
                        location: event.location,
                    }));

                days.push({ date, isCurrentMonth, isToday, isSelected, items, due, externalCalendarItems, scheduleItems });
            }
            weeksArray.push(days);
            currentWeekStart = addDays(currentWeekStart, 7);

            if (week === 4 && days.every(d => !d.isCurrentMonth)) {
                break;
            }
        }

        return weeksArray;
    }, [calendarStartDate, monthStart, planned, overrides, dueItems, calendarEvents, schedule]);

    const onScroll = useCallback((event: any) => {
        const target = event.target as HTMLDivElement;
        scrollPos.current = { top: target.scrollTop, left: target.scrollLeft };
    }, []);

    return (
        <div className="flex-1 min-h-0 relative">
            <div ref={scrollArea} className="h-full w-full overflow-auto" onScroll={onScroll}>
                <div className="p-4">
                    <div className="grid grid-cols-7 mb-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <div key={day} className="text-center font-semibold text-theme-1 text-sm py-2">
                                {day}
                            </div>
                        ))}
                    </div>
                    <div className="relative">
                        <div className="grid grid-cols-7">
                            {weeks.map((week, weekIndex) =>
                                week.map((day, dayIndex) => (
                                    <MonthDayCell
                                        key={`${weekIndex}-${dayIndex}`}
                                        date={day.date}
                                        isCurrentMonth={day.isCurrentMonth}
                                        isToday={day.isToday}
                                        items={day.items}
                                        dueItems={day.due}
                                        externalCalendarItems={day.externalCalendarItems}
                                        scheduleItems={day.scheduleItems}
                                        isSelected={day.isSelected}
                                        portal={portal}
                                    />
                                ))
                            )}
                        </div>
                        <MonthGridLines weeksCount={weeks.length} />
                    </div>
                </div>
            </div>
        </div>
    );
};

const MonthDayCell = ({ date, isCurrentMonth, isToday, isSelected, items, dueItems: dueItemsForDay, externalCalendarItems, scheduleItems }: any) => {
    const { courseData } = useCourses();
    const { updateSettings, setStartDate, modifyOverrides, modifyPlanned, dueItems } = usePlanner();
    const { loadSharedComponent } = useCommunication();
    const { activeCalendarDragItem, currentDragDelta, dropZone } = useDrag();
    const { setNodeRef } = useDroppable({
        id: `month-day-${date.toISOString()}`,
        data: {
            type: "month-day",
            date: date.toISOString(),
        }
    });

    const getCourseColor = (courseId: string) => courseData?.[courseId]?.color || "gray";
    const getCourseLabel = (courseId: string) => courseData?.[courseId]?.code || courseData?.[courseId]?.default || courseId || "Class";

    const buildAssignmentForEditor = (item: any) => {
        const matchingDue = (dueItems || []).find((due: any) => {
            const dueId = due?.current?.id ?? due?.original?.id;
            return dueId !== undefined && dueId !== null && String(dueId) === String(item.id);
        });

        if (matchingDue) return matchingDue;

        return {
            current: {
                id: item.id,
                title: item.title,
                course: item.course,
                plannedAt: item.plannedAt,
                duration: item.duration,
                status: item.status || 'incomplete',
            },
            original: {
                id: item.id,
                title: item.title,
                course: item.course,
                plannedAt: item.plannedAt,
                duration: item.duration,
                status: item.status || 'incomplete',
            },
        };
    };

    const handleAssignmentClick = (item: any) => {
        // Only bypass the editor for external calendar events; everything else should open the assignment editor.
        if (item.isGoogleEvent || item.isMicrosoftEvent || item.course === 'google_calendar' || item.course === 'microsoft_calendar') return;
        loadSharedComponent("AssignmentCreator", { assignment: buildAssignmentForEditor(item) });
    };

    const handleExternalClick = (item: any) => {
        if (item.htmlLink) window.open(item.htmlLink, '_blank');
    };

    const plannedCombined = [
        ...items.map((item: any) => ({ ...item, isExternal: false })),
        ...externalCalendarItems.map((item: any) => ({ ...item, isExternal: true })),
    ];

    // Hide the live item while dragging so only the ghost preview shows (prevents duplicates)
    const filteredPlannedCombined = useMemo(() => {
        if (!activeCalendarDragItem) return plannedCombined;
        const activeId = activeCalendarDragItem.id?.toString();
        return plannedCombined.filter((item) => item.id?.toString() !== activeId);
    }, [activeCalendarDragItem, plannedCombined]);

    // Show at most 2 items total (due items first, then planned). Everything else moves into "+N more".
    const classEntries = (scheduleItems || []).map((item: any) => ({
        type: 'class' as const,
        item: {
            ...item,
            startDate: item.startDate,
            endDate: item.endDate,
        },
    }));

    const combinedItems = [
        ...dueItemsForDay.map((item: any) => ({ type: 'due' as const, item })),
        ...filteredPlannedCombined.map((item: any) => ({ type: 'planned' as const, item })),
        ...classEntries,
    ];

    const getStartDate = (entry: { type: 'due' | 'planned' | 'class'; item: any }) => {
        if (entry.type === 'due') {
            return entry.item?.current?.due ? new Date(entry.item.current.due) : null;
        }
        if (entry.type === 'class') return entry.item?.startDate || null;
        return entry.item?.plannedAt ? new Date(entry.item.plannedAt) : null;
    };

    const priority = (entry: { type: 'due' | 'planned' | 'class' }) => (entry.type === 'due' ? 0 : 1);

    const sortedCombined = [...combinedItems].sort((a, b) => {
        const priorityDiff = priority(a) - priority(b);
        if (priorityDiff !== 0) return priorityDiff;

        const aDate = getStartDate(a);
        const bDate = getStartDate(b);
        if (aDate && bDate) return aDate.getTime() - bDate.getTime();
        if (aDate) return -1;
        if (bDate) return 1;
        return 0;
    });

    const visibleCombined = sortedCombined.slice(0, 2);
    const hiddenCount = Math.max(0, combinedItems.length - visibleCombined.length);

    const handleMoreClick = () => {
        setStartDate(date);
        updateSettings?.({ assignments_calendar_view_type: "day" });
    };

    const renderDueChip = (dueItem: any, idx: number) => {
        const courseColor = getCourseColor(dueItem.current?.course);
        const isComplete = dueItem.current?.status === "complete" || dueItem.current?.status === "graded";

        return (
            <button
                key={`due-${dueItem.current?.id}-${idx}`}
                className="flex items-center gap-2 rounded-[8px] px-[10px] py-[2px] text-xs font-semibold text-left cursor-pointer transition-all duration-150 border w-full min-w-0 hover:brightness-95 bg-theme-1 text-theme-0"
                style={{ borderColor: courseColor }}
                onClick={() => loadSharedComponent("AssignmentCreator", { assignment: dueItem })}
            >
                <span className="h-[10px] w-[10px] rounded-full flex-shrink-0" style={{ background: courseColor }} />
                <span className={`flex-1 min-w-0 truncate ${isComplete ? "line-through" : ""}`}>{dueItem.current?.title || "Due item"}</span>
                {dueItem.current?.due && <span className="text-[11px] text-theme-1 whitespace-nowrap">{format(new Date(dueItem.current.due), "h:mmaaa")}</span>}
            </button>
        );
    };

    const renderPlannedChip = (item: any, idx: number) => {
        const plannedDate = item.plannedAt ? new Date(item.plannedAt) : null;
        const courseColor = getCourseColor(item.course);
        const statusIcon = (() => {
            if (item?.status === "in-progress") return <ProgressSvg className="w-[14px] h-[14px]" />;
            if (item?.status === "complete" || item?.status === "graded") return <DoneSvg className="w-[14px] h-[14px]" />;
            return <IncompleteSvg className="w-[14px] h-[14px]" />;
        })();

        const handleStatusToggle = (e: React.MouseEvent) => {
            e.stopPropagation();
            const status = item?.status;
            const nextStatus =
                status === "complete" || status === "graded"
                    ? "incomplete"
                    : status === "incomplete"
                        ? "in-progress"
                        : "complete";

            // Update overrides (for due-linked items) and planned entry so all views stay in sync
            const id = item?.id;
            if (id !== undefined && id !== null) {
                modifyOverrides?.(id, { status: nextStatus });
                modifyPlanned?.({ ...item, status: nextStatus });
            }
        };

        return (
            <button
                key={`plan-${item.id}-${idx}`}
                className="flex items-center gap-2 rounded-[8px] px-[10px] py-[2px] text-xs font-semibold text-left cursor-pointer transition-all duration-150 border w-full min-w-0 hover:brightness-95"
                style={{ background: courseColor, borderColor: courseColor, color: "#fff" }}
                onClick={() => item.isExternal ? handleExternalClick(item) : handleAssignmentClick(item)}
            >
                <span
                    onClick={handleStatusToggle}
                    className="ml-[-2px] h-[16px] w-[16px] rounded-full flex-shrink-0 border border-white/80 bg-white/90 text-white/90 flex items-center justify-center hover:scale-105 transition-all duration-150"
                    style={{ color: courseColor }}
                    role="button"
                >
                    {statusIcon}
                </span>
                <span className={`flex-1 min-w-0 truncate translate-x-[-2px] ${item?.status === "complete" || item?.status === "graded" ? "line-through" : ""}`}>{item.title || "Planned"}</span>
                {plannedDate && <span className="text-[11px] text-white/80 whitespace-nowrap">{format(plannedDate, 'h:mmaaa')}</span>}
            </button>
        );
    };

    const renderClassChip = (item: any, idx: number) => {
        const courseColor = getCourseColor(item.course);
        const label = getCourseLabel(item.course);
        const startTime = item.startDate ? format(new Date(item.startDate), "h:mmaaa") : "";

        return (
            <div
                key={`class-${item.course}-${idx}`}
                className="flex items-center gap-2 rounded-[8px] px-[10px] py-[2px] text-[12px] font-semibold text-left border border-theme w-full min-w-0 bg-theme-1 text-theme-0"
            >
                <span className="h-[10px] w-[10px] rounded-full flex-shrink-0" style={{ background: courseColor }} />
                <div className="flex-1 min-w-0 flex items-center gap-2 justify-between">
                    <span className="truncate">{label}</span>
                    {startTime && <span className="text-[11px] text-theme-1 whitespace-nowrap">{startTime}</span>}
                </div>
            </div>
        );
    };

    const dateKey = useMemo(() => date.toDateString(), [date]);

    const showDragPreview = useMemo(() => {
        if (!activeCalendarDragItem) return false;
        if (dropZone?.type !== "month-day") return false;
        return dropZone.date === dateKey;
    }, [activeCalendarDragItem, dropZone, dateKey]);

    const dragPreview = useMemo(() => {
        if (!showDragPreview || !activeCalendarDragItem) return null;
        const courseColor = getCourseColor(activeCalendarDragItem.course);
        const plannedDate = currentDragDelta || (activeCalendarDragItem.plannedAt ? new Date(activeCalendarDragItem.plannedAt) : null);
        return (
            <div
                className="flex items-center gap-2 rounded-[8px] px-[10px] py-[2px] text-xs font-semibold text-left border border-dashed opacity-70"
                style={{ background: courseColor, borderColor: courseColor, color: "#fff" }}
            >
                <span className="flex-1 min-w-0 truncate">{activeCalendarDragItem.title || "Planned"}</span>
                {plannedDate && <span className="text-[11px] text-white/80 whitespace-nowrap">{format(plannedDate, 'h:mmaaa')}</span>}
            </div>
        );
    }, [activeCalendarDragItem, currentDragDelta, showDragPreview]);

    return (
        <div
            ref={setNodeRef}
            className={`p-2 min-h-[120px] bg-theme-0 ${!isCurrentMonth ? 'opacity-40' : ''}`}
        >
            <div className="flex items-center justify-between mb-2">
                <span
                    className={`text-sm font-semibold ${
                        isSelected
                            ? 'bg-theme-link rounded-full w-6 h-6 flex items-center justify-center shadow-sm'
                            : isToday
                                ? 'rounded-full w-6 h-6 flex items-center justify-center border border-theme text-theme-0'
                                : 'text-theme-0'
                    }`}
                    style={isSelected ? { color: "var(--bcbackground-0, #ffffff)" } : undefined}
                >
                    {format(date, 'd')}
                </span>
            </div>
            <div className="flex flex-col gap-1">
                {dragPreview}
                {visibleCombined.map(({ type, item }, idx) => {
                    if (type === 'due') return renderDueChip(item, idx);
                    if (type === 'class') return renderClassChip(item, idx);
                    return renderPlannedChip(item, idx);
                })}
                {hiddenCount > 0 && (
                    <button
                        className="text-xs text-theme-link font-semibold pl-1 hover:underline text-left"
                        onClick={handleMoreClick}
                    >
                        +{hiddenCount} more
                    </button>
                )}
            </div>
        </div>
    );
};

const MonthGridLines = ({ weeksCount }: { weeksCount: number }) => {
    const cellWidth = 100 / 7;
    const cellHeight = 120;

    const verticalLines = Array.from({ length: 6 }, (_, i) => (
        <div
            key={`v-line-${i}`}
            className="absolute h-full w-[1px] bg-theme-border"
            style={{ left: `${(i + 1) * cellWidth}%` }}
        />
    ));

    const horizontalLines = Array.from({ length: weeksCount - 1 }, (_, i) => (
        <div
            key={`h-line-${i}`}
            className="absolute w-full h-[1px] bg-theme-border"
            style={{ top: `${(i + 1) * cellHeight}px` }}
        />
    ));

    return (
        <div className="absolute inset-0 pointer-events-none">
            {verticalLines}
            {horizontalLines}
        </div>
    );
};

const DaySidebarSection = ({ title, emptyLabel, items, headerRight, contentTop }: { title: string, emptyLabel: string, items: { id: string, title: string, detail?: string, color?: string, onEdit?: () => void }[], headerRight?: React.ReactNode, contentTop?: React.ReactNode }) => {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 w-[calc(100%+24px)] -mr-6">
                <p className="text-xs font-semibold text-theme-1 uppercase tracking-[0.04em]">{title}</p>
                {headerRight}
            </div>
            {contentTop}
            {items.length === 0 ? (
                <p className="text-sm text-theme-1">{emptyLabel}</p>
            ) : (
                <div className="space-y-2 w-[calc(100%+24px)] -mr-6">
                    {items.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 rounded-md border border-theme px-3 py-2 w-full">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: item.color || "#9CA3AF" }} />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold truncate">{item.title}</p>
                                {item.detail && <p className="text-xs text-theme-1">{item.detail}</p>}
                            </div>
                            {item.onEdit && (
                                <button
                                    className="p-1 rounded-md hover:bg-theme-1 text-theme-1 hover:text-theme-0 transition-colors cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        item.onEdit?.();
                                    }}
                                    aria-label="Edit"
                                >
                                    <SquarePen className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SidebarFilters = ({
    showDue,
    showPlanned,
    showClasses,
    onToggle,
}: {
    showDue: boolean;
    showPlanned: boolean;
    showClasses: boolean;
    onToggle: (key: "due" | "planned" | "classes") => void;
}) => {
    const pill = (label: string, active: boolean, onClick: () => void) => (
        <button
            className={`flex items-center gap-2 px-3 py-1 rounded-full border text-sm transition-colors ${active ? "bg-theme-1 text-theme-0 border-theme" : "bg-theme-0 text-theme-0 border-theme hover:bg-theme-1"
                }`}
            onClick={onClick}
        >
            <span>{label}</span>
            {active && <Check className="h-4 w-4" />}
        </button>
    );

    return (
        <div className="flex flex-wrap items-center gap-3">
            {pill("Planned", showPlanned, () => onToggle("planned"))}
            {pill("Tasks", showDue, () => onToggle("due"))}
            {pill("Class", showClasses, () => onToggle("classes"))}
        </div>
    );
};

const PlannerSidebar = ({ selectedDate }: { selectedDate: Date }) => {
    const { setStartDate, planned, overrides, dueItems, settings } = usePlanner();
    const { schedule, courseData } = useCourses();
    const { loadSharedComponent, openScheduleEditor } = useCommunication();

    const [showDue, setShowDue] = useState(false);
    const [showPlanned, setShowPlanned] = useState(false);
    const [showClasses, setShowClasses] = useState(false);
    const [showUnplanned, setShowUnplanned] = useState(true);

    const date = useMemo(() => {
        const next = new Date(selectedDate);
        next.setHours(0, 0, 0, 0);
        return next;
    }, [selectedDate]);

    const areSameDay = useCallback((date1: Date, date2: Date) => {
        return date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
    }, []);

    const dueItemLookup = useMemo(() => {
        const map = new Map<string, any>();
        dueItems.forEach((item: any) => {
            const id = (item?.current?.id ?? item?.original?.id)?.toString();
            if (id) map.set(id, item);
        });
        return map;
    }, [dueItems]);

    const mergePlannedWithDetails = useCallback((key: string, plannedItem: any) => {
        const fallback = dueItemLookup.get(key);
        const source = fallback?.current || fallback?.original || {};
        return {
            id: key,
            ...plannedItem,
            course: plannedItem.course ?? source.course,
            title: plannedItem.title ?? source.title,
            status: plannedItem.status ?? source.status,
            duration: plannedItem.duration ?? source.duration ?? defaultTaskValues.duration,
        };
    }, [dueItemLookup]);

    const plannedItems = useMemo(() => {
        return Object.keys(planned).reduce((acc: AssignmentPlanned[], key: string) => {
            const item = planned[key];
            if (!item.plannedAt) return acc;
            const itemDate = new Date(item.plannedAt);
            if (!areSameDay(itemDate, date)) return acc;
            acc.push({
                ...mergePlannedWithDetails(key, planned[key]),
                ...(overrides[key]?.["title"] ? { title: overrides[key]?.["title"] } : {}),
                ...(overrides[key]?.["course"] ? { course: overrides[key]?.["course"] } : {}),
                ...(overrides[key]?.["status"] ? { status: overrides[key]?.["status"] } : {}),
            });
            return acc;
        }, [] as AssignmentPlanned[]);
    }, [areSameDay, date, mergePlannedWithDetails, overrides, planned]);

    const due = useMemo(() => {
        return dueItems.filter((item: any) => {
            const dueDate = item?.current?.due ? new Date(item.current.due) : null;
            return dueDate && areSameDay(dueDate, date);
        });
    }, [areSameDay, date, dueItems]);

    const scheduleItems = useMemo(() => schedule?.[date.getDay()] || [], [date, schedule]);
    const calendarViewType = settings?.assignments_calendar_view_type || "week";
    const weekDays = settings?.assignments_week_days || 7;
    const effectiveWeekDays = calendarViewType === "5day" ? 5 : weekDays;

    const unplannedRange = useMemo(() => {
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);

        let rangeStart = start;
        let rangeEnd = addDays(start, 1);

        if (calendarViewType === "month") {
            rangeStart = startOfMonth(start);
            rangeEnd = new Date(rangeStart.getFullYear(), rangeStart.getMonth() + 1, 1);
        } else if (calendarViewType === "week" || calendarViewType === "5day") {
            rangeEnd = addDays(start, effectiveWeekDays);
        }

        return { rangeStart, rangeEnd };
    }, [calendarViewType, date, effectiveWeekDays]);

    const unplannedItems = useMemo(() => {
        const { rangeStart, rangeEnd } = unplannedRange;
        return dueItems.filter((item: any) => {
            if (item?.current?.plannedAt !== null) return false;
            if (!item?.current?.due) return false;
            const dueDate = new Date(item.current.due);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate >= rangeStart && dueDate < rangeEnd;
        });
    }, [dueItems, unplannedRange]);

    const unplannedCount = useMemo(() => {
        return unplannedItems.length;
    }, [unplannedItems.length]);

    const buildPlannedForEditor = useCallback((plannedItem: any) => {
        const matchingDueItem = dueItems.find((due: any) => String(due?.current?.id) === String(plannedItem.id));
        if (matchingDueItem) return matchingDueItem;
        return {
            current: {
                id: plannedItem.id,
                title: plannedItem.title,
                course: plannedItem.course,
                plannedAt: plannedItem.plannedAt,
                duration: plannedItem.duration,
                status: plannedItem.status,
            },
            original: {
                id: plannedItem.id,
                title: plannedItem.title,
                course: plannedItem.course,
                plannedAt: plannedItem.plannedAt,
                duration: plannedItem.duration,
                status: plannedItem.status,
            },
        };
    }, [dueItems]);

    return (
        <div className="space-y-4">
            <MiniMonthPicker
                selectedDate={date}
                onSelectDate={(nextDate) => {
                    setStartDate(nextDate);
                }}
            />
            <SidebarFilters
                showDue={showDue}
                showPlanned={showPlanned}
                showClasses={showClasses}
                onToggle={(key) => {
                    const current = { due: showDue, planned: showPlanned, classes: showClasses };
                    const next = { ...current, [key]: !current[key] };
                    const noneOn = !next.due && !next.planned && !next.classes;
                    if (noneOn) {
                        setShowDue(false);
                        setShowPlanned(false);
                        setShowClasses(false);
                        return;
                    }
                    setShowDue(next.due);
                    setShowPlanned(next.planned);
                    setShowClasses(next.classes);
                }}
            />

            {(showPlanned || (!showDue && !showPlanned && !showClasses)) && (
                <DaySidebarSection
                    title="Planned Time"
                    emptyLabel="No planned items"
                    headerRight={(
                        <button
                            className="ml-auto flex items-center gap-1 rounded-full border border-theme px-2 py-0.5 text-xs text-theme-1 hover:bg-theme-1 hover:text-theme-0 transition-colors"
                            onClick={() => setShowUnplanned((prev) => !prev)}
                            aria-expanded={showUnplanned}
                            type="button"
                        >
                            <span>{unplannedCount} Unplanned</span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showUnplanned ? "rotate-180" : ""}`} />
                        </button>
                    )}
                    contentTop={(
                        <AnimatePresence initial={false}>
                            {showUnplanned ? (
                                <motion.div
                                    key="unplanned-dropdown"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: "easeInOut" }}
                                    className="overflow-visible"
                                >
                                    <div className="overflow-hidden rounded-lg border border-theme bg-theme-0 p-3 shadow-sm w-[calc(100%+24px)] -mr-6">
                                        <div className={`flex flex-col max-h-[440px] ${unplannedItems.length > 0 ? "min-h-[300px]" : ""}`}>
                                            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin pr-1 space-y-2 pb-2">
                                                {unplannedItems.length === 0 ? (
                                                    <p className="text-sm text-theme-1">No unplanned items</p>
                                                ) : (
                                                    unplannedItems.map((item: any) => (
                                                        <UnassignedTask key={item.current.id} item={item} />
                                                    ))
                                                )}
                                            </div>
                                            <div className="bg-theme-0 pt-2 shrink-0">
                                                <AutoScheduleButton tasks={unplannedItems} />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : null}
                        </AnimatePresence>
                    )}
                    items={plannedItems.map((item: any) => {
                        const start = item.plannedAt ? new Date(item.plannedAt) : null;
                        const end = start ? addMinutes(start, item.duration || defaultTaskValues.duration) : null;
                        const course = courseData?.[item.course || ""];
                        return {
                            id: `plan-${item.id}`,
                            title: item.title || "Planned item",
                            detail: start && end ? `${format(start, "h:mmaaa")} - ${format(end, "h:mmaaa")}` : "",
                            color: course?.color,
                            onEdit: () => loadSharedComponent("AssignmentCreator", { assignment: buildPlannedForEditor(item) }),
                        };
                    })}
                />
            )}

            {(showDue || (!showDue && !showPlanned && !showClasses)) && (
                <DaySidebarSection
                    title="Tasks Due"
                    emptyLabel="Nothing due"
                    items={due.map((item: any) => {
                        const dueDate = item?.current?.due ? new Date(item.current.due) : null;
                        const course = courseData?.[item.current?.course || ""];
                        return {
                            id: `due-${item.current?.id}`,
                            title: item.current?.title || "Due item",
                            detail: dueDate ? format(dueDate, "h:mmaaa") : "",
                            color: course?.color,
                            onEdit: () => loadSharedComponent("AssignmentCreator", { assignment: item }),
                        };
                    })}
                />
            )}

            {(showClasses || (!showDue && !showPlanned && !showClasses)) && (
                <DaySidebarSection
                    title="Class Time"
                    emptyLabel="No classes"
                    items={scheduleItems.map((item: any) => {
                        const start = new Date(date);
                        start.setHours(Math.floor(item.start / 60), item.start % 60, 0, 0);
                        const end = new Date(date);
                        end.setHours(Math.floor(item.end / 60), item.end % 60, 0, 0);
                        const course = courseData?.[item.course];
                        return {
                            id: `${item.course}-${item.start}`,
                            title: course?.code || course?.default || item.course || "Class",
                            detail: `${format(start, "h:mmaaa")} - ${format(end, "h:mmaaa")}`,
                            color: course?.color,
                            onEdit: () => openScheduleEditor(item.course, course?.code || course?.default),
                        };
                    })}
                />
            )}
        </div>
    );
};

const SingleDayView = ({ rightPanelWidth, sidebarMinWidth, viewportWidth }: { rightPanelWidth: number; sidebarMinWidth: number; viewportWidth: number }) => {
    const { startDate, blockWidth, timeWidth, planned, overrides, dueItems, blockHeight, scrollArea, scrollPos } = usePlanner();
    const { schedule } = useCourses();
    const lastAutoScrollKey = useRef<string | null>(null);

    const calendarStartDate = useMemo(() => startDate, [startDate]);
    const calendarEndDate = useMemo(() => addDays(startDate, 1), [startDate]);
    const { events: calendarEvents } = useCalendarEvents(calendarStartDate, calendarEndDate);

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    // Fill missing planned metadata (title/course/status/duration) from the corresponding due item
    const dueItemLookup = useMemo(() => {
        const map = new Map<string, any>();
        dueItems.forEach((item: any) => {
            const id = (item?.current?.id ?? item?.original?.id)?.toString();
            if (id) map.set(id, item);
        });
        return map;
    }, [dueItems]);

    const mergePlannedWithDetails = (key: string, plannedItem: any) => {
        const fallback = dueItemLookup.get(key);
        const source = fallback?.current || fallback?.original || {};
        return {
            id: key,
            ...plannedItem,
            course: plannedItem.course ?? source.course,
            title: plannedItem.title ?? source.title,
            status: plannedItem.status ?? source.status,
            duration: plannedItem.duration ?? source.duration ?? defaultTaskValues.duration,
        };
    };

    const areSameDay = (date1: Date, date2: Date) => {
        return date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
    };

    const dayData = useMemo(() => {
        const date = new Date(startDate);
        date.setHours(0, 0, 0, 0);

        const scheduleItems = schedule?.[date.getDay()] || [];

        const items: AssignmentPlanned[] = Object.keys(planned).reduce((acc: AssignmentPlanned[], key: string) => {
            const item = planned[key];
            if (!item.plannedAt) return acc;
            const itemDate = new Date(item.plannedAt);
            if (!areSameDay(itemDate, date)) return acc;
            acc.push({
                ...mergePlannedWithDetails(key, planned[key]),
                ...(overrides[key]?.["title"] ? { title: overrides[key]?.["title"] } : {}),
                ...(overrides[key]?.["course"] ? { course: overrides[key]?.["course"] } : {}),
                ...(overrides[key]?.["status"] ? { status: overrides[key]?.["status"] } : {}),
            });
            return acc;
        }, [] as AssignmentPlanned[]);

        const due = dueItems.filter((item: any) => {
            const dueDate = item?.current?.due ? new Date(item.current.due) : null;
            return dueDate && areSameDay(dueDate, date);
        });

        const externalCalendarItems = calendarEvents
            .filter((event) => {
                return areSameDay(event.startDate, date) && !event.isAllDay;
            })
            .map((event) => ({
                id: event.id,
                title: event.title,
                plannedAt: event.startDate.toISOString(),
                duration: event.duration,
                course: event.source === 'google' ? 'google_calendar' : 'microsoft_calendar',
                isGoogleEvent: event.isGoogleEvent,
                isMicrosoftEvent: event.isMicrosoftEvent,
                calendarColor: event.calendarColor,
                htmlLink: event.htmlLink,
                location: event.location,
            }));

        return { date, items, due, externalCalendarItems, scheduleItems };
    }, [startDate, planned, schedule, calendarEvents, dueItems]);

    const takeHomeData = useMemo(() => {
        const date = dayData.date;
        const map = new Map<string, any[]>();
        const key = date.toISOString();
        const takeHomes = (dueItems || []).filter((item: any) => {
            if (!isTakeHomeAssignment(item)) return false;
            const dueDate = item?.current?.due ? new Date(item.current.due) : null;
            if (!dueDate) return false;
            return areSameDay(dueDate, date);
        });
        map.set(key, takeHomes);
        const maxCount = takeHomes.length;
        return { map, maxCount };
    }, [dayData.date, dueItems]);

    const dueRowHeight = useMemo(() => {
        if (!takeHomeData.maxCount) return MIN_DUE_ROW_HEIGHT;
        const stackHeight = (takeHomeData.maxCount * DUE_CHIP_HEIGHT) + (Math.max(0, takeHomeData.maxCount - 1) * DUE_CHIP_GAP);
        return Math.max(MIN_DUE_ROW_HEIGHT, (DUE_ROW_PADDING_Y * 2) + stackHeight);
    }, [takeHomeData]);

    const onScroll = useCallback(
        (event: any) => {
            const target = event.target as HTMLDivElement;
            scrollPos.current = { top: target.scrollTop, left: target.scrollLeft };
        },
        [],
    );

    const [scrollAreaWidth, setScrollAreaWidth] = useState<number>(0);

    useEffect(() => {
        if (!scrollArea.current || !Number.isFinite(blockHeight) || blockHeight <= 0) return;
        const key = `day-${startDate.toDateString()}`;
        if (lastAutoScrollKey.current === key) return;
        lastAutoScrollKey.current = key;

        requestAnimationFrame(() => {
            const now = new Date();
            const desiredHour = (now.getHours() + (now.getMinutes() / 60)) - 2;
            const desiredTop = Math.max(0, desiredHour * blockHeight);
            const node = scrollArea.current;
            if (!node) return;
            const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
            const nextTop = Math.min(maxScroll, desiredTop);
            node.scrollTop = nextTop;
            scrollPos.current = { top: nextTop, left: node.scrollLeft };
        });
    }, [blockHeight, scrollArea, startDate, scrollPos]);

    useEffect(() => {
        const node = scrollArea.current;
        if (!node || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width || 0;
            setScrollAreaWidth(width);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [scrollArea]);

    // Stretch day view width so due chips span more horizontal space and fill remaining room
    const dayColumnWidth = useMemo(() => {
        const fallbackLeft = viewportWidth - rightPanelWidth;
        const available = scrollAreaWidth || fallbackLeft;
        const paddingRight = 16; // matches pr-4 on the grid
        const measured = available - timeWidth - paddingRight;
        return Math.max(blockWidth, measured);
    }, [blockWidth, rightPanelWidth, scrollAreaWidth, timeWidth, viewportWidth]);

    return (
        <div className="flex-1 min-h-0 relative">
            <div className="flex h-full gap-0">
                <div
                    className="hidden md:flex flex-col rounded-l-lg border border-theme bg-theme-0 p-4 pr-10 overflow-y-auto"
                    style={{
                        flex: "0 0 auto",
                        width: `${rightPanelWidth}px`,
                        minWidth: `${sidebarMinWidth}px`,
                        paddingBottom: "90px",
                    }}
                >
                    <PlannerSidebar selectedDate={dayData.date} />
                </div>
                <div className="min-w-0" style={{ flex: "1 1 auto" }}>
                    <div ref={scrollArea} className="h-full w-full overflow-auto rounded-r-lg border border-theme border-l-0 bg-theme-0" onScroll={onScroll}>
                        <div className="grid grid-cols-[auto_1fr] flex-1 h-[calc(var(--block-height) * 24)] relative pr-4">
                            <div
                                className="bg-theme-0 border-b border-theme sticky top-0 left-0 z-40"
                                style={{ width: `${timeWidth}px`, height: `${HEADER_HEIGHT}px` }}
                            />
                            <div
                                className="sticky top-0 left-0 bg-theme-0 border-b border-theme z-40 h-[40px] relative bg-theme-0 flex items-center justify-center"
                                style={{ width: `${dayColumnWidth}px`, minWidth: `${dayColumnWidth}px`, "--block-width": `${dayColumnWidth}px` } as React.CSSProperties}
                            >
                                <div className="flex items-center gap-[5px]">
                                    <p className="font-medium">{format(dayData.date, 'eee')}</p>
                                    <p className="text-theme-1">{format(dayData.date, 'MMM d')}</p>
                                    {areSameDay(todayDate, dayData.date) && <p className="text-sm text-theme-link font-medium">Today</p>}
                                </div>
                            </div>
                            <div
                                className="sticky left-0 bg-theme-0 border-b border-theme z-30 flex items-center justify-center px-3 border-r border-theme"
                                style={{ top: `${HEADER_HEIGHT}px`, height: `${dueRowHeight}px`, width: `${timeWidth}px` }}
                            >
                                <p className="text-sm font-semibold">Due</p>
                            </div>
                            <div
                                className="sticky left-0 bg-theme-0 border-b border-theme z-30"
                                style={{
                                    top: `${HEADER_HEIGHT}px`,
                                    height: `${dueRowHeight}px`,
                                    width: `${dayColumnWidth}px`,
                                    minWidth: `${dayColumnWidth}px`,
                                }}
                            >
                                <DueRowContent dates={[dayData.date]} takeHomeMap={takeHomeData.map} blockWidth={dayColumnWidth} />
                            </div>
                            <div
                                className="sticky top-0 left-0 bg-theme-0 z-25 border-r border-theme"
                                style={{ top: `${HEADER_HEIGHT + dueRowHeight}px`, width: `${timeWidth}px` }}
                            >
                                <CalendarTimeIndicators />
                            </div>
                            <div className="flex-1 relative flex min-w-[400px] overflow-hidden" style={{ width: `${dayColumnWidth}px`, minWidth: `${dayColumnWidth}px`, "--block-width": `${dayColumnWidth}px` } as React.CSSProperties}>
                                <CalendarRowLines />
                                <div className="absolute flex h-full w-full [&>*]:!min-w-full [&>*]:!max-w-full">
                                    <CalendarColumn
                                        start={dayData.date}
                                        today={areSameDay(todayDate, dayData.date)}
                                        showTimeIndicator={true}
                                        items={dayData.items}
                                        dueItems={dayData.due}
                                        calendarEvents={dayData.externalCalendarItems}
                                        scheduleItems={dayData.scheduleItems}
                                        key={dayData.date.toISOString()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const PlannerView = () => {
    const { scrollPos, scrollArea, dueItems, setNumCols, numCols, blockHeight, blockWidth, settings, timeWidth, startDate } = usePlanner();
    const calendarViewType = settings?.assignments_calendar_view_type || "week";
    const weekDays = settings?.assignments_week_days || 7;
    const effectiveWeekDays = calendarViewType === "5day" ? 5 : weekDays;
    const lastAutoScrollKey = useRef<string | null>(null);

    const { trackEvent, withEventCooldown } = useCommunication();
    const { viewportWidth, rightPanelWidth, sidebarMinWidth } = useSidebarSizing();

    const scrollTo = useCallback(
        (hour: number) => {
            const scrollPosition = Math.max(0, (hour - 1) * blockHeight);
            const viewport = scrollArea.current?.querySelector("[data-radix-scroll-area-viewport]");

            if (viewport) {
                viewport.scrollTo({
                    top: scrollPosition,
                    behavior: "instant",
                });
            }
        },
        [scrollArea],
    );

    useEffect(() => {
        withEventCooldown("planner_view_opened", async () => {
            await trackEvent("planner_view_opened");
        }, 30);
    }, []);

    useEffect(() => {
        if (calendarViewType !== "week" && calendarViewType !== "5day") return;
        if (!scrollArea.current || !Number.isFinite(blockHeight) || blockHeight <= 0) return;

        const key = `${calendarViewType}-${startDate.toDateString()}`;
        if (lastAutoScrollKey.current === key) return;
        lastAutoScrollKey.current = key;

        requestAnimationFrame(() => {
            const now = new Date();
            const desiredHour = (now.getHours() + (now.getMinutes() / 60)) - 2;
            const desiredTop = Math.max(0, desiredHour * blockHeight);
            const node = scrollArea.current;
            if (!node) return;
            const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
            const nextTop = Math.min(maxScroll, desiredTop);
            node.scrollTop = nextTop;
            scrollPos.current = { top: nextTop, left: node.scrollLeft };
        });
    }, [calendarViewType, blockHeight, scrollArea, startDate, scrollPos]);

    const onScroll = useCallback(
        (event: any) => {
            const target = event.target as HTMLDivElement;
            scrollPos.current = { top: target.scrollTop, left: target.scrollLeft };
            setScrollLeft(target.scrollLeft);

            if (target.scrollWidth - target.clientWidth - target.scrollLeft < 300) {
                const increment = calendarViewType === "5day" ? 1 : effectiveWeekDays;
                setNumCols((prev: number) => prev + increment);
            }
        },
        [setNumCols, effectiveWeekDays],
    );

    const [scrollLeft, setScrollLeft] = useState(0);
    const visibleColumnOffset = useMemo(() => Math.floor(Math.abs(scrollLeft) / blockWidth), [scrollLeft, blockWidth]);
    const totalColumns = Math.max(numCols, effectiveWeekDays);
    const contentWidth = totalColumns * blockWidth;

    const { takeHomeMap, maxTakeHomeCount, visibleDates } = useMemo(() => {
        const map = new Map<string, any[]>();
        const dates = Array.from({ length: effectiveWeekDays }, (_, i) => {
            const date = addDays(startDate, i + visibleColumnOffset);
            date.setHours(0, 0, 0, 0);
            return date;
        });

        dueItems.forEach((item: any) => {
            if (isTakeHomeAssignment(item) === false) return;
            if (!item?.current?.due) return;

            const dueDate = new Date(item.current.due);
            dueDate.setHours(0, 0, 0, 0);
            const key = dueDate.toISOString();
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(item);
        });

        let maxCount = 0;
        dates.forEach((date) => {
            const key = date.toISOString();
            const count = map.get(key)?.length || 0;
            if (count > maxCount) maxCount = count;
        });

        return { takeHomeMap: map, maxTakeHomeCount: maxCount, visibleDates: dates };
    }, [dueItems, effectiveWeekDays, startDate, visibleColumnOffset]);

    const dueRowHeight = useMemo(() => {
        if (!maxTakeHomeCount) return MIN_DUE_ROW_HEIGHT;
        const stackHeight = (maxTakeHomeCount * DUE_CHIP_HEIGHT) + (Math.max(0, maxTakeHomeCount - 1) * DUE_CHIP_GAP);
        return Math.max(MIN_DUE_ROW_HEIGHT, (DUE_ROW_PADDING_Y * 2) + stackHeight);
    }, [maxTakeHomeCount]);

    const renderWithSidebar = (content: React.ReactNode, sidebarContent: React.ReactNode) => {
        return (
            <div className="flex-1 min-h-0 relative">
                <div className="flex h-full gap-0">
                    <div
                        className="hidden md:flex flex-col rounded-l-lg border border-theme bg-theme-0 p-4 pr-10 overflow-y-auto"
                        style={{
                            flex: "0 0 auto",
                            width: `${rightPanelWidth}px`,
                            minWidth: `${sidebarMinWidth}px`,
                            paddingBottom: "90px",
                        }}
                    >
                        {sidebarContent}
                    </div>
                    <div className="min-w-0 flex-1">
                        {content}
                    </div>
                </div>
            </div>
        );
    };

    const renderCalendarContent = () => {
        if (calendarViewType === "day") {
            return <SingleDayView rightPanelWidth={rightPanelWidth} sidebarMinWidth={sidebarMinWidth} viewportWidth={viewportWidth} />;
        } else if (calendarViewType === "month") {
            return renderWithSidebar(<MonthGridView />, <PlannerSidebar selectedDate={startDate} />);
        }

        return renderWithSidebar(
            <div ref={scrollArea} className="h-full w-full overflow-auto" onScroll={onScroll}>
                <div className={`grid grid-cols-[auto_1fr] flex-1 h-[calc(var(--block-height) * 24)] relative`}>
                    <div
                        className="bg-theme-0 border-b border-theme sticky top-0 left-0 z-40"
                        style={{ width: `${timeWidth}px`, height: `${HEADER_HEIGHT}px` }}
                    />
                    <div
                        className="sticky top-0 left-0 bg-theme-0 border-b border-theme z-40 relative bg-theme-0"
                        style={{ width: `${contentWidth}px`, minWidth: `${contentWidth}px`, height: `${HEADER_HEIGHT}px` }}
                    >
                        <PlannerColumnHeaders scrollLeft={scrollLeft} />
                    </div>
                    <div
                        className="sticky left-0 bg-theme-0 border-b border-theme z-40 flex items-center justify-center px-3 border-r border-theme"
                        style={{ top: `${HEADER_HEIGHT}px`, height: `${dueRowHeight}px`, width: `${timeWidth}px` }}
                    >
                        <p className="text-sm font-semibold">Due</p>
                    </div>
                    <div
                        className="sticky left-0 bg-theme-0 border-b border-theme z-30"
                        style={{
                            top: `${HEADER_HEIGHT}px`,
                            height: `${dueRowHeight}px`,
                            width: `${contentWidth}px`,
                            minWidth: `${contentWidth}px`,
                        }}
                    >
                        <DueRowContent
                            dates={visibleDates}
                            takeHomeMap={takeHomeMap}
                            blockWidth={blockWidth}
                            offsetLeft={visibleColumnOffset * blockWidth}
                        />
                    </div>
                    <div
                        className="sticky left-0 bg-theme-0 border-r border-theme"
                        style={{ top: `${HEADER_HEIGHT + dueRowHeight}px`, width: `${timeWidth}px`, zIndex: 5 }}
                    >
                        <CalendarTimeIndicators />
                    </div>
                    <div className="flex-1 relative flex" style={{ width: `${contentWidth}px`, minWidth: `${contentWidth}px` }}>
                        <PlannerColumns scrollLeft={scrollLeft} />
                    </div>
                </div>
            </div>,
            <PlannerSidebar selectedDate={startDate} />
        );
    };

    return (
        <>
            <NonCalendarDragPreview />
            {renderCalendarContent()}
        </>
    )
}

const PlannerColumns = ({ scrollLeft }: { scrollLeft: number }) => {
    const { startDate, blockWidth, planned, overrides, dueItems, settings } = usePlanner();
    const { schedule } = useCourses();

    const calendarViewType = settings?.assignments_calendar_view_type || "week";
    const weekDays = settings?.assignments_week_days || 7;
    const effectiveWeekDays = calendarViewType === "5day" ? 5 : weekDays;

    // Calculate date range for calendar events (current view + buffer for scrolling)
    const calendarStartDate = useMemo(() => addDays(startDate, -7), [startDate]);
    const calendarEndDate = useMemo(() => addDays(startDate, 30), [startDate]); // Load a month ahead for smooth scrolling
    const { events: calendarEvents } = useCalendarEvents(calendarStartDate, calendarEndDate);

    console.log("calendarEvents for planner view", calendarEvents);

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    // Fill missing planned metadata (title/course/status/duration) from the corresponding due item
    const dueItemLookup = useMemo(() => {
        const map = new Map<string, any>();
        dueItems.forEach((item: any) => {
            const id = (item?.current?.id ?? item?.original?.id)?.toString();
            if (id) map.set(id, item);
        });
        return map;
    }, [dueItems]);

    const mergePlannedWithDetails = (key: string, plannedItem: any) => {
        const fallback = dueItemLookup.get(key);
        const source = fallback?.current || fallback?.original || {};
        return {
            id: key,
            ...plannedItem,
            course: plannedItem.course ?? source.course,
            title: plannedItem.title ?? source.title,
            status: plannedItem.status ?? source.status,
            duration: plannedItem.duration ?? source.duration ?? defaultTaskValues.duration,
        };
    };

    const minHour = useRef(Infinity);

    // Calculate which set of columns should be visible based on scroll position
    const visibleColumnOffset = Math.floor(Math.abs(scrollLeft) / blockWidth);

    const columns = useMemo(
        () =>
            Array.from({ length: effectiveWeekDays }, (_, i) => {
                // Add the visible column offset to get the actual date we should show
                const date: Date = addDays(startDate, i + visibleColumnOffset);
                date.setHours(0, 0, 0, 0);

                const scheduleItems = schedule?.[date.getDay()] || [];

                const items: AssignmentPlanned[] = Object.keys(planned).reduce((acc: AssignmentPlanned[], key: string) => {
                    const item = planned[key];
                    if (!item.plannedAt) return acc;
                    const itemDate = new Date(item.plannedAt);
                    if (!areSameDay(itemDate, date)) return acc;
                    //if (itemDate.getHours() < minHour.current) minHour.current = itemDate.getHours();
                    acc.push({
                        ...mergePlannedWithDetails(key, planned[key]),
                        ...(overrides[key]?.["title"] ? { title: overrides[key]?.["title"] } : {}),
                        ...(overrides[key]?.["course"] ? { course: overrides[key]?.["course"] } : {}),
                        ...(overrides[key]?.["status"] ? { status: overrides[key]?.["status"] } : {}),
                    });
                    return acc;
                }, [] as AssignmentPlanned[]);

                const due = dueItems.filter((item: any) => {
                    if (isTakeHomeAssignment(item)) return false;
                    const dueDate = item?.current?.due ? new Date(item.current.due) : null;
                    return dueDate && areSameDay(dueDate, date);
                });

                // Filter calendar events (Google & Microsoft) for this date
                const externalCalendarItems = calendarEvents
                    .filter((event) => {
                        return areSameDay(event.startDate, date) && !event.isAllDay;
                    })


                return (
                    <CalendarColumn
                        start={date}
                        today={areSameDay(todayDate, date)}
                        items={items}
                        dueItems={due}
                        calendarEvents={externalCalendarItems}
                        scheduleItems={scheduleItems}
                        key={date.toISOString()} // Use date as key to force re-render when date changes
                    />
                )
            }), [startDate, visibleColumnOffset, planned, schedule, calendarEvents, dueItems, effectiveWeekDays]);



    return (
        <div className="absolute flex h-full" style={{ "left": `${visibleColumnOffset * blockWidth}px` }}>
            <CalendarRowLines />
            <CalendarColumnLines />
            {columns}
        </div>
    );
}

const CalendarColumnLines = () => {

    const { blockWidth, settings } = usePlanner();
    const calendarViewType = settings?.assignments_calendar_view_type || "week";
    const weekDays = settings?.assignments_week_days || 7;
    const effectiveWeekDays = calendarViewType === "5day" ? 5 : weekDays;
    const lines = Array.from({ length: effectiveWeekDays }, (_, i) => {
        return <div className="absolute w-[1px] h-full bg-theme-border" style={{ "left": ((i + 1) * blockWidth) + 'px' }} key={"row-line-" + i} />
    });

    return (
        <div className={`absolute top-0 left-0 w-full h-full z-1`}>
            {lines}
        </div>
    )
}
